import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './integrated-auth';
import { apiRequest } from '@/lib/queryClient';
import { useChatNotifications, type ChatNotification } from '@/components/chat/hooks/use-chat-notifications';

// Chat target types matching YapZap's chat system
export type ChatTarget =
  | { type: 'league'; leagueId: number; leagueName?: string; isOwner?: boolean; chatEnabled?: boolean; chatImagesAllowed?: boolean; themeColor?: string; headerImageUrl?: string }
  | { type: 'daily-room'; roomId: number; roomName: string; roomMembers?: Array<Record<string, unknown>> }
  | { type: 'daily-lobby'; roundId: number }
  | { type: 'scene'; sceneId: number; sceneName?: string; isMember?: boolean }
  | { type: 'community'; channelId: number; channelName?: string; categoryId?: number | null; categoryType?: string | null; isMember?: boolean; emoji?: string | null }
  | { type: 'hub' };

interface NotificationLeague {
  id: number;
  name: string;
  themeColor?: string | null;
  theme_color?: string | null;
}

interface ChatContextType {
  // Sheet state
  isChatOpen: boolean;
  openChat: () => void;
  closeChat: () => void;

  // Chat target (context-aware)
  chatTarget: ChatTarget;
  setChatTarget: (target: ChatTarget | null) => void;

  // Navigation: true when user drilled into a chat from the hub
  openedFromHub: boolean;
  goBackToHub: () => void;

  // Unread count
  totalUnreadCount: number;
  chatNotifications: ChatNotification[];
  markLeagueAsRead: (leagueId: number) => void;
}

const ChatContext = createContext<ChatContextType | null>(null);

export function useChatContext() {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error('useChatContext must be used within ChatContextProvider');
  }
  return ctx;
}

// Try to use chat context without throwing (for components outside the provider)
export function useChatContextSafe() {
  return useContext(ChatContext);
}

function areChatTargetsEqual(a: ChatTarget | null, b: ChatTarget | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.type !== b.type) return false;

  switch (a.type) {
    case 'hub':
      return true;
    case 'league':
      return b.type === 'league'
        && a.leagueId === b.leagueId
        && a.leagueName === b.leagueName
        && a.isOwner === b.isOwner
        && a.chatEnabled === b.chatEnabled
        && a.chatImagesAllowed === b.chatImagesAllowed
        && a.themeColor === b.themeColor
        && a.headerImageUrl === b.headerImageUrl;
    case 'daily-room':
      return b.type === 'daily-room'
        && a.roomId === b.roomId
        && a.roomName === b.roomName;
    case 'daily-lobby':
      return b.type === 'daily-lobby'
        && a.roundId === b.roundId;
    case 'scene':
      return b.type === 'scene'
        && a.sceneId === b.sceneId
        && a.sceneName === b.sceneName
        && a.isMember === b.isMember;
    case 'community':
      return b.type === 'community'
        && a.channelId === b.channelId
        && a.channelName === b.channelName
        && a.categoryId === b.categoryId
        && a.categoryType === b.categoryType
        && a.isMember === b.isMember
        && a.emoji === b.emoji;
    default:
      return false;
  }
}

function getRouteTarget(location: string): ChatTarget | null {
  // /leagues/:id → league chat
  const leagueMatch = location.match(/^\/leagues\/(\d+)/);
  if (leagueMatch) {
    return { type: 'league', leagueId: parseInt(leagueMatch[1], 10) };
  }

  // /daily or /daily/:roundId → daily chat (page will register specific target)
  if (location.startsWith('/daily')) {
    return null; // Let the daily page register the specific target (room vs lobby)
  }

  // /scene/:id → scene chat (page will register specific target)
  const sceneMatch = location.match(/^\/scene\/(\d+)/);
  if (sceneMatch) {
    return { type: 'scene', sceneId: parseInt(sceneMatch[1], 10) };
  }

  return null;
}

export function ChatContextProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [location] = useLocation();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [manualTarget, setManualTarget] = useState<ChatTarget | null>(null);
  const [openedFromHub, setOpenedFromHub] = useState(false);

  // Fetch user's leagues for unread tracking
  const { data: userLeagues } = useQuery<NotificationLeague[]>({
    queryKey: ['/api/leagues/user/active'],
    enabled: isAuthenticated,
    staleTime: 60000, // Cache for 1 minute - don't refetch too aggressively
  });

  // Map leagues to the format useChatNotifications expects
  const leaguesForNotifications = useMemo(() => {
    if (!userLeagues || !Array.isArray(userLeagues)) return [];
    return userLeagues.map((league) => ({
      id: league.id,
      name: league.name,
      themeColor: league.themeColor || league.theme_color,
    }));
  }, [userLeagues]);

  // Track unread counts across all league chats
  const { totalUnreadCount, chatNotifications, markLeagueAsRead } = useChatNotifications({
    leagues: leaguesForNotifications,
    enabled: isAuthenticated && leaguesForNotifications.length > 0,
  });

  // Derive chat target: manual override (from page registration) > route-based > hub
  const routeTarget = useMemo(() => getRouteTarget(location), [location]);

  const chatTarget: ChatTarget = useMemo(() => {
    if (manualTarget) return manualTarget;
    if (routeTarget) return routeTarget;
    return { type: 'hub' };
  }, [manualTarget, routeTarget]);

  // Clear manual target when route changes
  useEffect(() => {
    setManualTarget(null);
    setIsChatOpen(false);
    setOpenedFromHub(false);
  }, [location]);

  // Mark league as read when opening its chat
  useEffect(() => {
    if (isChatOpen && chatTarget.type === 'league') {
      markLeagueAsRead(chatTarget.leagueId);
    }
  }, [isChatOpen, chatTarget, markLeagueAsRead]);

  const openChat = useCallback(() => {
    if (!isAuthenticated) return;
    setIsChatOpen(true);
  }, [isAuthenticated]);

  const closeChat = useCallback(() => {
    setIsChatOpen(false);
    setOpenedFromHub(false);
  }, []);

  const goBackToHub = useCallback(() => {
    setManualTarget({ type: 'hub' });
    setOpenedFromHub(false);
  }, []);

  // Track current chatTarget type via ref so setChatTarget can read it without re-creating
  const chatTargetRef = useRef(chatTarget);
  chatTargetRef.current = chatTarget;

  const setChatTarget = useCallback((target: ChatTarget | null) => {
    // Only mark "opened from hub" when user is currently viewing the hub and drills into a chat
    if (isChatOpen && target && target.type !== 'hub' && chatTargetRef.current.type === 'hub') {
      setOpenedFromHub(true);
    }

    setManualTarget((currentTarget) => {
      if (areChatTargetsEqual(currentTarget, target)) {
        return currentTarget;
      }
      return target;
    });
  }, [isChatOpen]);

  const value = useMemo<ChatContextType>(() => ({
    isChatOpen,
    openChat,
    closeChat,
    chatTarget,
    setChatTarget,
    openedFromHub,
    goBackToHub,
    totalUnreadCount,
    chatNotifications,
    markLeagueAsRead,
  }), [isChatOpen, openChat, closeChat, chatTarget, setChatTarget, openedFromHub, goBackToHub, totalUnreadCount, chatNotifications, markLeagueAsRead]);

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}
